// Animated stereogram pipeline (client side):
//   depth frames -> temporal filter -> global range -> parallel frame render.

import { loadFont } from '../depth/fonts';
import { buildDepthOps } from '../depth/ops';
import { BatchEta, estimateMs, recordMs } from '../eta';
import { fitSize } from '../image';
import { processDepth, sourceDepth, texturePixels, type Inputs } from '../pipeline';
import type { StudioSettings } from '../state.svelte';
import {
  toDepth16,
  type Depth16,
  type DepthData,
  type DepthOp,
  type PatternMotion,
  type RgbaData,
  type TemporalFilter,
} from '../types';
import { batchPool, interactivePool } from '../wasm/pool';
import { FrameStore } from './frames';
import type { AnimLimits } from './limits';
import type { AnimSettings } from './settings.svelte';
import { renderTextAnimation } from './textAnim';
import { videoToDepth, type VideoInfo } from './video';
import { initGeometry } from '../scene/geometry';
import { renderSceneAnimation } from '../scene/render';
import { sceneStore } from '../scene/store.svelte';
import { estimateMs as est } from '../eta';

export interface SequenceInput {
  frames: Depth16[];
  name: string;
}

export interface AnimInputs extends Inputs {
  sequence: SequenceInput | null;
  /** Morph endpoints; `null` A means "the static source from Create". */
  morph?: { a: DepthData | null; b: DepthData | null };
  video: { file: File; info: VideoInfo } | null;
}

export type Stage = 'depth' | 'smooth' | 'render';

export interface Progress {
  stage: Stage;
  done: number;
  total: number;
  etaMs: number;
  elapsedMs: number;
}

export interface AnimResult {
  store: FrameStore;
  depth: Depth16[];
  fps: number;
  loop: 'loop' | 'ping_pong';
  ms: number;
}

/** Output size and frame count after applying limits. */
export function planAnimation(a: AnimSettings, inputs: AnimInputs, limits: AnimLimits) {
  const fps = Math.min(Math.max(1, a.fps), limits.maxFps);
  let w: number;
  let h: number;
  let frames: number;
  const seq = inputs.sequence?.frames;
  const video = inputs.video?.info;
  if (a.source === 'video' && video) {
    const side = Math.min(Math.max(a.width, a.height), limits.maxSide);
    [w, h] = fitSize(video.width, video.height, side, side, true);
    frames = Math.min(Math.floor(video.duration * fps), limits.maxFrames, Math.floor(limits.maxSeconds * fps));
  } else if (a.source === 'sequence' && seq?.length) {
    const side = Math.min(Math.max(a.width, a.height), limits.maxSide);
    [w, h] = fitSize(seq[0].width, seq[0].height, side, side, true);
    frames = Math.min(seq.length, limits.maxFrames, Math.floor(limits.maxSeconds * fps));
  } else {
    [w, h] = fitSize(a.width, a.height, limits.maxSide, limits.maxSide);
    // A scene animates over its own timeline.
    const duration = a.source === 'scene' ? sceneStore.current.timeline.duration || a.duration : a.duration;
    frames = Math.min(Math.round(fps * Math.min(duration, limits.maxSeconds)), limits.maxFrames);
  }
  return { width: w, height: h, frames: Math.max(1, frames), fps };
}

export function motionOf(a: AnimSettings): PatternMotion {
  return a.motion === 'scroll' ? { kind: 'scroll', speed: a.scrollSpeed } : { kind: a.motion };
}

export function filterOf(a: AnimSettings): TemporalFilter {
  switch (a.filter) {
    case 'ema':
      return { kind: 'ema', alpha: a.emaAlpha, threshold: a.filterThreshold };
    case 'median':
      return { kind: 'median', radius: a.medianRadius, threshold: a.filterThreshold };
    default:
      return { kind: 'none' };
  }
}

/** Sequence-wide range (optionally percentile-clipped) over a pixel subsample. */
export function globalRange(frames: Depth16[], clip: number): [number, number] {
  const sample: number[] = [];
  let lo = 65535;
  let hi = 0;
  for (const f of frames) {
    const step = Math.max(1, Math.floor(f.data.length / 20000));
    for (let i = 0; i < f.data.length; i += step) {
      const v = f.data[i];
      if (v < lo) lo = v;
      if (v > hi) hi = v;
      if (clip > 0) sample.push(v);
    }
  }
  if (clip > 0 && sample.length) {
    sample.sort((a, b) => a - b);
    lo = sample[Math.floor(clip * (sample.length - 1))];
    hi = sample[Math.ceil((1 - clip) * (sample.length - 1))];
  }
  return [lo / 65535, Math.max(hi, lo + 1) / 65535];
}

/** Per-frame ops: global remap instead of per-frame normalisation (no "breathing"). */
export function frameOps(s: StudioSettings, range: [number, number] | null, width: number, height: number): DepthOp[] {
  const ops = buildDepthOps({ ...s.depth, normalize: false }, width, height);
  if (range) ops.splice(1, 0, { op: 'remap', lo: range[0], hi: range[1] });
  return ops;
}

function aborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
}

export interface DepthFrames {
  depth: Depth16[];
  /** Per-frame ops for client rendering (sequence-wide normalisation baked in). */
  ops: DepthOp[];
  /** Per-frame source colours (video). */
  colour: RgbaData[] | null;
}

/**
 * Stage 1 (+2): depth frames for the animation source, temporally filtered.
 * `renderTailMs` is the expected time of whatever runs afterwards (for ETA).
 */
export async function generateDepthFrames(
  a: AnimSettings,
  s: StudioSettings,
  inputs: AnimInputs,
  plan: { width: number; height: number; frames: number; fps: number },
  renderTailMs: number,
  onProgress: (p: Progress) => void,
  signal?: AbortSignal,
): Promise<DepthFrames> {
  const t0 = performance.now();
  const { width, height, frames: n, fps } = plan;
  const pool = batchPool();
  const workers = pool.size;
  const mpx = (width * height) / 1e6;
  const perDepth =
    a.source === 'text' || a.source === 'scene'
      ? estimateMs('text_depth', mpx)
      : a.source === 'procedural'
        ? estimateMs('depth_process', mpx)
        : 0;
  // 1. Depth frames.
  let depth: Depth16[];
  let ops: DepthOp[];
  let colour: RgbaData[] | null = null;
  const depthStart = performance.now();
  const depthEta = (done: number) => {
    const el = performance.now() - depthStart;
    const per = done > 0 ? el / done : perDepth;
    return (n - done) * per + renderTailMs;
  };
  onProgress({ stage: 'depth', done: 0, total: n, etaMs: depthEta(0), elapsedMs: 0 });
  if (a.source === 'morph') {
    const resolve = async (d: DepthData | null) => {
      if (d) return processDepth(interactivePool(), s, d, width, height);
      const src = await sourceDepth(interactivePool(), s, inputs, width, height);
      if (!src) throw new Error('no static source in "Create"');
      return processDepth(interactivePool(), s, src, width, height);
    };
    const A = await resolve(inputs.morph?.a ?? null);
    if (!inputs.morph?.b) throw new Error('choose depth map B');
    const B = await resolve(inputs.morph.b);
    let done = 0;
    depth = await Promise.all(
      Array.from({ length: n }, (_, i) =>
        pool.call('morph', { a: A, b: B, t: n > 1 ? i / (n - 1) : 0 }).then((d) => {
          done++;
          onProgress({ stage: 'depth', done, total: n, etaMs: depthEta(done), elapsedMs: performance.now() - t0 });
          return d;
        }),
      ),
    );
    // Both endpoints are already processed.
    ops = [{ op: 'resize', width, height }];
  } else if (a.source === 'text') {
    const font = await loadFont(s.text.font);
    depth = await renderTextAnimation(
      font,
      s.text,
      a.preset,
      width,
      height,
      n,
      (done) =>
        onProgress({ stage: 'depth', done, total: n, etaMs: depthEta(done), elapsedMs: performance.now() - t0 }),
      signal,
    );
    recordMs('text_depth', mpx * n, performance.now() - depthStart);
    const range = s.depth.normalize ? globalRange(depth, s.depth.clip) : null;
    ops = frameOps(s, range, width, height);
  } else if (a.source === 'scene') {
    await initGeometry();
    const frames = await renderSceneAnimation(
      sceneStore.current,
      fps,
      n,
      width,
      height,
      (done) =>
        onProgress({ stage: 'depth', done, total: n, etaMs: depthEta(done), elapsedMs: performance.now() - t0 }),
      signal,
    );
    depth = frames.map(toDepth16);
    recordMs('text_depth', mpx * n, performance.now() - depthStart);
    const range = s.depth.normalize ? globalRange(depth, s.depth.clip) : null;
    ops = frameOps(s, range, width, height);
  } else if (a.source === 'procedural') {
    let base: DepthData = { width, height, data: new Float32Array(width * height) };
    if (a.proceduralBase === 'studio') {
      const src = await sourceDepth(interactivePool(), s, inputs, width, height);
      if (src) base = await processDepth(interactivePool(), s, src, width, height);
    }
    let done = 0;
    depth = await Promise.all(
      Array.from({ length: n }, (_, i) =>
        pool.call('procedural', { depth: base, anim: a.procedural, t: i / n }).then((d) => {
          done++;
          onProgress({ stage: 'depth', done, total: n, etaMs: depthEta(done), elapsedMs: performance.now() - t0 });
          return d;
        }),
      ),
    );
    recordMs(
      'depth_process',
      mpx * n,
      ((performance.now() - depthStart) * workers) / Math.max(1, Math.min(workers, n)),
    );
    // The base is already processed; frames are rendered as they are.
    ops = [{ op: 'resize', width, height }];
  } else {
    if (a.source === 'video') {
      if (!inputs.video) throw new Error('no video loaded');
      const perFrame = est('ml:depth-anything-v2-small:wasm', 1);
      const v = await videoToDepth(
        inputs.video.file,
        fps,
        n,
        Math.max(width, height),
        (done) => {
          const el = performance.now() - depthStart;
          const per = done ? el / done : perFrame;
          onProgress({
            stage: 'depth',
            done,
            total: n,
            etaMs: (n - done) * per + renderTailMs,
            elapsedMs: performance.now() - t0,
          });
        },
        signal,
      );
      depth = v.depth;
      colour = v.colour;
    } else {
      const seq = inputs.sequence?.frames;
      if (!seq?.length) throw new Error('no depth sequence loaded');
      depth = seq.slice(0, n);
    }
    // 2. Temporal filtering (sequence-wide, single worker).
    const filter = filterOf(a);
    if (filter.kind !== 'none') {
      onProgress({
        stage: 'smooth',
        done: 0,
        total: 1,
        etaMs: renderTailMs,
        elapsedMs: performance.now() - t0,
      });
      const smoothed = await interactivePool().call('smoothSequence', {
        frames: depth.map((d) => d.data.slice()),
        width: depth[0].width,
        height: depth[0].height,
        filter,
      });
      depth = smoothed.map((data) => ({ width: depth[0].width, height: depth[0].height, data }));
    }
    const range = s.depth.normalize ? globalRange(depth, s.depth.clip) : null;
    ops = frameOps(s, range, width, height);
  }
  aborted(signal);
  return { depth, ops, colour };
}

export async function generateAnimation(
  a: AnimSettings,
  s: StudioSettings,
  inputs: AnimInputs,
  limits: AnimLimits,
  onProgress: (p: Progress) => void,
  signal?: AbortSignal,
): Promise<AnimResult> {
  const t0 = performance.now();
  const { width, height, frames: n, fps } = planAnimation(a, inputs, limits);
  const pool = batchPool();
  const workers = pool.size;
  const mpx = (width * height) / 1e6;
  const renderUnit = mpx * s.stereo.oversample;
  const perRender = estimateMs('stereogram', renderUnit);
  const perDepth =
    a.source === 'text'
      ? estimateMs('text_depth', mpx)
      : a.source === 'procedural'
        ? estimateMs('depth_process', mpx)
        : 0;
  const onAbort = () => pool.cancelAll();
  signal?.addEventListener('abort', onAbort);

  try {
    const { depth, ops, colour } = await generateDepthFrames(
      a,
      s,
      inputs,
      { width, height, frames: n, fps },
      (n * perRender) / workers,
      onProgress,
      signal,
    );

    // 3. Render frames in parallel.
    const store = new FrameStore(width, height, n);
    const eta = new BatchEta(n, (n * perRender) / workers);
    const renderStart = performance.now();
    let done = 0;
    const motion = motionOf(a);
    const textureImage = texturePixels(s.texture, inputs);
    // "From source" colours follow the video frame by frame.
    const frameTexture = (i: number) =>
      s.texture.kind === 'source' && colour ? colour[Math.min(i, colour.length - 1)] : textureImage;
    onProgress({ stage: 'render', done: 0, total: n, etaMs: eta.remainingMs(0), elapsedMs: performance.now() - t0 });
    await Promise.all(
      depth.map((d, i) =>
        pool
          .call('renderFrame', {
            depth: d,
            ops,
            params: s.stereo,
            texture: s.texture,
            motion,
            frame: i,
            textureImage: frameTexture(i),
          })
          .then((img) => {
            store.set(i, img);
            done++;
            onProgress({
              stage: 'render',
              done,
              total: n,
              etaMs: eta.remainingMs(done),
              elapsedMs: performance.now() - t0,
            });
          }),
      ),
    );
    // Per-frame cost as if single-threaded, comparable with static renders.
    recordMs('stereogram', renderUnit * n, (performance.now() - renderStart) * Math.min(workers, n));
    return { store, depth, fps, loop: a.loop, ms: performance.now() - t0 };
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }
}
